import { MESSAGE } from "../App/constants.js";
import {
    deepClone,
    getQueryParamValue, getVPCServiceWithoutSlash,
    is_localhost, isInIframe,
    parseJSON, publicChatData, publicChatToken,
    sortAndConcatenateMessages
} from "../App/utils.js";
import { onLevelerImageRotation } from "../InputTools/ImageToText.js";
import {getKBFileURL, getRequestId, saveImageToS3} from "./utils";
import {decryptKBItem, encryptKBDataWithoutPrivateKey, encryptKBItem} from "../App/cryptoUtils";
import {blockAutoscroll, generateMsgId, trackReaction} from "../Chat/utils";
import {getBalanceJWT} from "./Ledger";

import {playAudio} from "../Chat/TextToSpeechPlayer";
import {db} from "../Engines/Dexie";
import {convertIndexedItem, decryptItemByAttributes} from "../Item/utils";
import {getKB} from "./KB/KBAPI";
import {ChatModels} from "../constants";

const CHAT_API_URL = getVPCServiceWithoutSlash('chat') || ( is_localhost ? ('http://localhost:8012') : (!isInIframe() ? 'https://chat.openkbs.com' : 'https://chat-service.openkbs.com') )

export let ON_PREMISES_CHAT_API_URL = null;
export let ON_PREMISES_CHAT_MODELS = null;

export const setOnPremisesChatURL = (o) => ON_PREMISES_CHAT_API_URL = o
export const setOnPremisesChatModelsConst = (models) => {
    ON_PREMISES_CHAT_MODELS = models
}

const getChatAPIURL = (model) => {
    if (model) {
        const MODELS = ON_PREMISES_CHAT_MODELS || ChatModels
        const isOnPremises = MODELS[model]?.isOnPremises;
        return isOnPremises ? ON_PREMISES_CHAT_API_URL : CHAT_API_URL;
    } else {
        return ON_PREMISES_CHAT_API_URL || CHAT_API_URL
    }
};

export async function requestChatStream(params, signal, KBData, rawPrompt = false) {
    /** params
     * {
     *   "chatPayload": {
     *     "model": "gpt-4o",
     *     "messages": [
     *       {
     *         "role": "user",
     *         "content": "test",
     *         "userId": "User_xxx",
     *         "msgId": "1725794570706-267385"
     *       }
     *     ],
     *     "temperature": 0,
     *     "stream": true
     *   },
     *   "AESKey": "xxx",
     *   "OpenAIKey": "",
     *   "chatId": "1725794534061-282111"
     * }
     */
    const { accountId, walletPublicKey } = KBData;
    const walletPrivateKey = decryptKBItem(KBData?.walletPrivateKey);

    const token = localStorage.getItem('kbJWT');

    return fetch(getChatAPIURL(params.chatPayload?.model), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            ...params,
            token: token ? token : undefined,
            publicChatToken: publicChatToken ? publicChatToken : undefined,
            rawPrompt,
            balanceJWT: publicChatData ? undefined : (await getBalanceJWT(accountId, walletPrivateKey, walletPublicKey)),
            predefinedAccountId: publicChatData ? publicChatData?.accountId : accountId,
            injectedKBData: ON_PREMISES_CHAT_API_URL ? encryptKBDataWithoutPrivateKey(KBData) : undefined // ON_PREMISES_CHAT_API instance do not have access to kb table
        }),
        signal
    }).catch(error => {
        if (error.name === 'AbortError') {
        } else {
            console.error('Fetch error:', error);
        }
    });
}

export const getChat = async (chatId) => {
    const chat = await chatActionRequest({ action: 'getChat', chatId});
    if (!chat?.data) return;
    const data = chat.data;

    if (data?.title) data.title = decryptKBItem(data.title)
    if (data?.chatInstructions) data.chatInstructions = decryptKBItem(data.chatInstructions)
    return data
};

export const listChats = async (limit = 25, lastEvaluatedKey = null) => {
    const payload = { 
        action: 'listChats',
        limit
    };
    if (lastEvaluatedKey) {
        payload.lastEvaluatedKey = lastEvaluatedKey;
    }
    
    const response = await chatActionRequest(payload);
    if (!response?.data) return { data: [], lastEvaluatedKey: null };

    // Backend now always returns {data: [...], lastEvaluatedKey: ...}
    const chatsData = response.data.data || response.data;
    const chats = chatsData.map(chat => ({ ...chat, title: decryptKBItem(chat.title) }));
    
    return {
        data: chats,
        lastEvaluatedKey: response.data.lastEvaluatedKey || null
    };
};

export const getAllUnreadMessages = ( ) => chatActionRequest({action: 'getAllUnreadMessages'});

export const getUnreadMessages = ( chatId ) => chatActionRequest({action: 'getUnreadMessages', chatId});

export const deleteOrphanedUnreadMessages = ( chatId ) => {
    chatActionRequest({action: 'deleteUnreadMessages', chatId});
}

export const chatMessageRemoveReaction = ( chatId, msgId, emojiId ) => {
    blockAutoscroll()
    trackReaction(msgId, emojiId)
    return chatActionRequest({
        action: 'chatMessageRemoveReaction',
        chatId,
        msgId,
        emojiId
    });
}

export const chatMessageAddReaction = ( chatId, msgId, emojiId ) => {
    blockAutoscroll()
    trackReaction(msgId, emojiId)
    return chatActionRequest({
        action: 'chatMessageAddReaction',
        chatId,
        msgId,
        emojiId
    });
}


export const chatEditMessage = ( chatId, msgId, content ) =>
    chatActionRequest({
        action: 'chatEditMessage',
        content: encryptKBItem(content),
        chatId,
        msgId
    });

export const createChat = (title, messages, chatId) =>
    chatActionRequest({
        action: 'createChat',
        title: encryptKBItem(title),
        messages: messages.map(msg => ({...msg, content: encryptKBItem(msg.content)})),
        chatId
    });

export const deleteChat = (chatId) => chatActionRequest({ action: 'deleteChat', chatId});

export const getChatMessagesCache = {};

export const updateChat = ({title, chatId, chatInstructions, chatIcon, chatModel}) =>
    chatActionRequest({
        action: 'updateChat',
        title: title ? encryptKBItem(title) : undefined,
        chatInstructions: (chatInstructions || chatInstructions === '') ? encryptKBItem(chatInstructions) : undefined,
        chatIcon,
        chatModel,
        chatId
    });

export const chatAddMessages = async (chatId, messages) => {
    return chatActionRequest({
        action: 'chatAddMessages',
        chatId,
        injectedKBData: ON_PREMISES_CHAT_API_URL ? encryptKBDataWithoutPrivateKey(await getKB()) : undefined,
        messages: messages.map(msg => ({...msg, content: encryptKBItem(msg.content)}))
    })
};

export const chatDeleteMessage = (chatId, msgId) => chatActionRequest({action: 'chatDeleteMessage', chatId, msgId})

export const getChatMessages = ({chatId, limit = 25, lastEvaluatedKey, signal}) => {
    return chatActionRequest({ action: 'getChatMessages', chatId, limit, lastEvaluatedKey }, signal)
        .then(data => {
            if (data?.data?.messages?.length) {
                data.data.messages = data.data.messages.map(msg => ({ ...msg, content: decryptKBItem(msg.content) }));
            }
            if (chatId && data) getChatMessagesCache[chatId] = data;
            return data;
        })
};

export function chatActionRequest(payload, signal, API_URL) {
    const token = localStorage.getItem('kbJWT');
    const publicChatToken = localStorage.getItem('publicChatToken');

    const bodyData = token
        ? { ...payload, token }
        : publicChatToken
            ? { ...payload, publicChatToken }
            : payload;

    return fetch((API_URL || ON_PREMISES_CHAT_API_URL || CHAT_API_URL), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(bodyData),
        signal
    })
        .then(response => {
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            return response.json();
        })
        .catch(error => {
            // console.error('There has been a problem with your fetch operation:', error);
        });
}

export function warmupChat(KBData) {
    // const { accountId, walletPublicKey } = KBData;
    // const walletPrivateKey = decryptKBItem(KBData?.walletPrivateKey);
    // getAccountBalances(accountId, walletPrivateKey, walletPublicKey)
    // return fetch(API_URL + '?warmup=1');
}

const indexedDBSeen = {};

export async function handleData (
    response,
    data, 
    chatMessages, 
    parts, 
    nextImageFiles, 
    resolve, 
    setIsStreaming, 
    setSystemAlert,
    setMessages,
    RequestChatAPI,
    onImageToTextResponse,
    imageFiles,
    kbId,
    chatId,
    KB
    ) {

    let lastMessage = chatMessages[chatMessages.length - 1];

    // Insert empty assistant message on demand
    if (data?.role === 'assistant' && lastMessage?.role !== 'assistant') {
        chatMessages.push({role: 'assistant', content: '', msgId: generateMsgId()});
        lastMessage = chatMessages[chatMessages.length - 1]
    }

    if (data.error && data.statusCode === 401) {
        setSystemAlert({msg: 'Your session has expired, please login!', type: 'error', duration: 10000});
        window.dispatchEvent(new Event('unauthorizedError'));
        return;
    }
    else if (data.error && typeof data.error === 'string') {
        let error = data?.error;
        if (error?.includes('Bedrock is unable to process your request')) {
            setSystemAlert({
                msg: 'The AI model is currently unavailable, choose another model from the top menu or try again later.',
                type: 'error',
                duration: 10000
            });
        } else {
            setSystemAlert({
                msg: error,
                type: 'error',
                duration: 10000
            });
        }

        setIsStreaming(false);
        resolve(data);
        return;
    }

    const contentData = parseJSON(data.content);

    // @Todo - remove redundant message from SAVED_CHAT_IMAGE (we need to find the core problem)
    // for (let i = Math.max(chatMessages.length - 3, 0); i < chatMessages.length - 1; i++) {
    //     if (chatMessages[i].role === 'system' && chatMessages[i + 1].role === 'system' && chatMessages[i].content === chatMessages[i + 1].content) {
    //         chatMessages.splice(i + 1, 1);
    //         i--;
    //     }
    // }

    const dexieCommandRegex = /dexieDB\['\w+'\](?:\.\w+\(.*?\))*\.toArray\(\)/;
    const matchDexie = lastMessage?.content.match(dexieCommandRegex);
    // here role has changed (assistant->system), system EVENT_STARTED, so we can save the lastMessage
    if ( // This is still not used @Todo to remove it
        lastMessage?.role === 'assistant' &&
        matchDexie?.[0] &&
        !indexedDBSeen[lastMessage.msgId]
    ) {
        // db['page'].where('price').between(10,20)
        indexedDBSeen[lastMessage.msgId] = true
        const queryString = matchDexie?.[0];

        // Execute the command using eval
        let data;
        try {
            const dexieDB = db
            dexieDB.KBData = KB;
            data = await eval(queryString);
        } catch (error) {
            console.error("Error executing command:", error);
        }

        const responseMessage = {
            role: 'system',
            content: JSON.stringify({
                type: MESSAGE.INDEXED_DB_RESPONSE,
                data: data.map(o => {
                    const {item, meta} = convertIndexedItem(o, 'page');
                    return decryptItemByAttributes(item, meta.attributes);
                })
            })
        }

        RequestChatAPI(deepClone([...chatMessages, responseMessage]));

        chatAddMessages(chatId, [deepClone(responseMessage)])
        // chatMessages.push(responseMessage)
    } else if (data?.role && data?.role !== lastMessage.role && contentData?._meta_type === MESSAGE.EVENT_STARTED) {
        /**
         * Assistant Add Message (Assistant lastMessage without Done)
         */
        if (chatId && lastMessage?.role === 'assistant' && lastMessage?.content) {
            chatAddMessages(chatId, [deepClone(lastMessage)])
        }

        chatMessages.push({role: data?.role, content: data.content})
    } else if (parseJSON(lastMessage.content)?._meta_type === MESSAGE.EVENT_STARTED &&
        contentData?._meta_type === MESSAGE.EVENT_FINISHED
    ) {

        if (contentData?.type === 'CONTINUE') {
            /**
             * REMOVE EVENT_START on EVENT_FINISHED finished with continue
             * Here we remove the start of the event, that finished without match (returns CONTINUE)
             * Removes EVENT_STARTED from chatMessages when EVENT_FINISHED in contentData returns CONTINUE
             * #Example1 onRequest does not match anything from the user input, so it returns { type: 'CONTINUE' }
             */
            chatMessages.pop();

            parts.push(data); // we push this CONTINUE event as message part to be later processed (see #flow1 below)
        } else if (contentData?.data?.audioContent) {
            playAudio(contentData?.data)
            delete contentData.data.audioContent;
            chatMessages.pop();
        } else if (contentData?.type === MESSAGE.SAVED_CHAT_IMAGE && contentData?.imageSrc) {
            const imageSrc = contentData?.imageSrc;
            const filename = getRequestId() + '.png';
            await saveImageToS3({kbId, imageSrc, filename});
            delete contentData.imageSrc;
            contentData.imageUrl = getKBFileURL(filename, kbId)
            chatMessages[chatMessages.length - 1].content = JSON.stringify(contentData);
            if (!chatMessages[chatMessages.length - 1].msgId) chatMessages[chatMessages.length - 1].msgId = generateMsgId();
            if (contentData?._meta_actions?.includes(MESSAGE.REQUEST_CHAT_MODEL)) {
                RequestChatAPI(deepClone([...chatMessages, deepClone(chatMessages[chatMessages.length - 1])]));
            }
            chatAddMessages(chatId, [deepClone(chatMessages[chatMessages.length - 1])])
            chatMessages.pop();
        } else if (typeof contentData  === 'object') {
            if (data?.role === 'system') {
                /**
                 * System Add Message
                 */
                const systemMsgResponse = {role: data?.role, content: data?.content, msgId: generateMsgId()}

                /**
                 * REMOVE EVENT_START on any "system" object
                 *
                 * Here we remove EVENT_STARTED on event that finished without continue (some data)
                 * Example: onResponse handler matches some command from the LLM output, so here we remove the EVENT_STARTED msg
                 */
                chatMessages.pop()

                chatMessages.push(systemMsgResponse)

                if (contentData?._meta_actions?.includes(MESSAGE.REQUEST_CHAT_MODEL)) {
                    RequestChatAPI(deepClone(chatMessages));
                }

                /**
                 * After systemMsgResponse is stored to DynamoDB, it will be added to the chat via WS event
                 * After #fix1 these messages will already be added
                 */
                chatAddMessages(chatId, [deepClone(systemMsgResponse)])
            }
        } else {
            chatMessages.pop();
        }

    } else if (typeof data.content === 'string') {
        // #flow1
        if (parseJSON(parts[parts.length-1]?.content)?.type === "CONTINUE") {
            // #1 assign empty string to content so next messages can append to it onResponse
            parts[parts.length-1].content = '';
        }

        parts.push(data);

        const lastContentData = parseJSON(lastMessage?.content);
        // Never overwrite last message with valid data (includes results array for RESPONSE type)
        if (lastContentData?.data === undefined && lastContentData?.error === undefined && lastContentData?.imageSrc === undefined && lastContentData?.results === undefined) {
            lastMessage.content = sortAndConcatenateMessages(parts);
            if (data.role) lastMessage.role = data.role; // override lastMessage role
        }

    }

    setMessages([...chatMessages]);
    
    if (!data.done) return;
    if (is_localhost && getQueryParamValue('debug')) console.log(chatMessages)
    // #onResponseDone
    setIsStreaming(false);

    const jsonMessage = parseJSON(chatMessages?.[chatMessages?.length - 1]?.content);

    if (jsonMessage?.type === MESSAGE.IMAGE_ROTATION) {
        onLevelerImageRotation({
            KB,
            setSystemAlert,
            chatMessages,
            nextImageFiles,
            imageFiles,
            onImageToTextResponse,
            leveler: jsonMessage.leveler
        })
    }

    resolve(data);
}
